import { For, Show, createSignal, createEffect, createMemo, onCleanup, batch } from 'solid-js';
import '@styles/Tagger.css';

import { apiEndpoints } from '@config/env';
import { getData, putData, formatTime, getTimezoneForDate, localTimeInTimezoneToUtcDate } from '@utils/global';
import { debug, warn, error as logError } from '@utils/console';
import { persistantStore } from '@store/persistantStore';
import { unifiedDataStore, type Event as DatasetEvent } from '@store/unifiedDataStore';
import { setSelectedTime, requestTimeControl, setIsManualTimeChange, releaseTimeControl, timeWindow, selectedTime, isPlaying, smoothPlaybackTimeForTrack, getDisplayWindowReferenceTime, shouldApplyPlaybackTimeWindow, setTagsSeekMapCenter } from '@store/playbackStore';
import {
  setSelectedRange,
  setSelectedRanges,
  setSelectedEvents,
  setHasSelection,
  setTriggerSelection,
  setIsCut,
  isCut,
  cutEvents,
  selectedRange,
} from '@store/selectionStore';
import {
  EXPLORE_USER_EVENTS_CHANGED_EVENT,
  exploreRowKind,
  exploreCanonicalUtcIso,
  fetchExploreTagRows,
  normalizeExploreTargetDate,
  parseExploreUtcIso,
  type ExploreTagMergedRow,
  putUserEventExploreUpdate,
  type ExploreUserEventRow,
} from '@services/userEventsExploreService';
import { sourcesStore } from '@store/sourcesStore';
import { fetchDatasetTimezone } from '@store/datasetTimezoneStore';
import { setSelectedSources as setFilterStoreSelectedSources } from '@store/filterStore';
import { getExploreVisibleBoundsIncludingPlaybackMs } from '@utils/cutSelectionTimeRange';
import { getFleetRaceLegFilteredBoundsMs, getFleetRaceLegBoundsFromTagRows } from '@utils/fleetTimeWindow';
import {
  isTrainingHourMode,
  selectedLegsTimeseries,
  selectedRacesTimeseries,
} from '@store/filterStore';
import { EXPLORE_NOTE_FOCUS_PADDING_MS } from '@services/exploreEventsTableService';
import {
  parseDatasetEventTags,
  noteTextFromDatasetEvent,
  tagThumbsFromTags,
  datasetThumbsState,
} from '@utils/datasetEventTags';
import {
  buildTaggerAuthorOrdinalMap,
  taggerAuthorNormalizedKey,
  taggerAuthorPillStylesByOrdinal,
} from '@utils/taggerAuthorPill';
import {
  buildExploreUserEventUpdateFromForm,
  datetimeLocalValueToIso,
  isoToDatetimeLocalValue,
  movedIntervalTimes,
  prestartTimeIsoFromExploreRow,
  raceNumberFromExploreTags,
  raceTagNumber,
  TAGGER_TEST_COMMENT_MAX_LENGTH,
  taggerClampTestCommentInput,
  taggerEventTypeAllowsEmptyComment,
  taggerEventTypeIsPrestart,
  taggerUserEventShowsCommentField,
  taggerValidateTestComment,
} from '@utils/taggerEventEditForm';
import { buildTagExploreMapMarkers } from '@utils/tagsExploreMapMarkers';
import {
  MAP_TRACK_GAP_THRESHOLD_MS,
  buildCoverageSegmentsMs,
  extractSortedTimestampsMs,
  isMsInsideSegments,
} from '@utils/mapTrackGapCoverage';
import { setTagsExploreMapMarkers } from '@store/tagsExploreMapMarkersStore';
import { mediaAvailabilityService } from '@services/mediaAvailabilityService';
import { FaSolidThumbsDown, FaSolidThumbsUp } from 'solid-icons/fa';
import TagsExploreEditTimeFields from '@components/explore/TagsExploreEditTimeFields';
import TagsExploreEditCrewFields from '@components/explore/TagsExploreEditCrewFields';
import { extractRowSourceId, extractRowSourceName } from '@utils/selectionRangeHelpers';
import {
  applyCrewCode,
  CREW_SEATS,
  crewTagEditPatch,
  crewTagEditValuesFromTags,
  crewTagsFromEditValues,
  crewTagVisibleForSource,
  emptyCrewTagEditValues,
  formatCrewTagSummary,
  type CrewTagEditField,
  type CrewTagEditValues,
} from '@utils/crewCode';
import { ensureCrewConfigForLineup, fetchCrews } from '@services/crewService';
import { fetchCrewRoster } from '@services/crewRosterService';
import { toastStore } from '@store/toastStore';

/** Second click within this window counts as double-click (range select); otherwise single-click seeks. */
const TAG_CLICK_DOUBLE_MS = 280;

/** Ctrl/Cmd+click from tags: timeline brush width on each side of the seek time (60s total). */
const CTRL_SEEK_HALF_SPAN_MS = 30_000;

/** Clips shorter than this (ms) appear as MEDIA tags (explore list + map markers). */
const SHORT_MEDIA_MAX_DURATION_MS = 60_000;

function isCtrlOrMetaClick(e: MouseEvent): boolean {
  return e.ctrlKey || e.metaKey;
}

const ISO_WITH_TZ_RE = /(z|[+-]\d{2}:?\d{2})$/i;
const SIMPLE_LOCAL_DT_RE = /^(\d{4})-(\d{2})-(\d{2})[t ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,3})?)?$/i;

/** Legacy-only: naive display strings without API *_utc fields. */
function parseIsoToDateWithTimezone(
  iso: string | null | undefined,
  timezone?: string | null
): Date | null {
  if (iso == null) {return null;}
  const raw = String(iso).trim();
  if (!raw) {return null;}

  // Already timezone-aware (Z or +/-hh:mm): parse directly as UTC instant.
  if (ISO_WITH_TZ_RE.test(raw)) {
    const d = new Date(raw);
    return Number.isFinite(d.getTime()) ? d : null;
  }

  // Naive datetime: interpret in dataset/day timezone when available.
  const normalized = raw.replace(' ', 'T');
  const m = normalized.match(SIMPLE_LOCAL_DT_RE);
  if (m && timezone) {
    const dateStr = `${m[1]}-${m[2]}-${m[3]}`;
    const timeStr = `${m[4]}:${m[5]}:${m[6] || '00'}`;
    const d = localTimeInTimezoneToUtcDate(dateStr, timeStr, timezone);
    if (d && Number.isFinite(d.getTime())) {
      return d;
    }
  }

  // Fallback: browser parsing.
  const d = new Date(normalized);
  return Number.isFinite(d.getTime()) ? d : null;
}

function normalizeIsoForTimezone(
  iso: string | null | undefined,
  timezone?: string | null
): string | null {
  const canonical = parseExploreUtcIso(iso);
  if (canonical) {return canonical;}
  const d = parseIsoToDateWithTimezone(iso, timezone);
  return d ? d.toISOString() : null;
}

/** Canonical UTC ISO for sort/seek/selection: prefer API *_utc, else legacy display parsing. */
function canonicalExploreTimeField(
  utc: string | null | undefined,
  display: string | null | undefined,
  timezone?: string | null
): string | null {
  return exploreCanonicalUtcIso(utc, display) ?? normalizeIsoForTimezone(display, timezone);
}

function parseUtcToDate(iso: string | null | undefined): Date | null {
  const canonical = parseExploreUtcIso(iso);
  if (!canonical) {return null;}
  return new Date(canonical);
}

function focusTimeMsFromIso(
  iso: string | null | undefined,
  timezone?: string | null
): number | null {
  const d = parseUtcToDate(iso) ?? parseIsoToDateWithTimezone(iso, timezone);
  if (!d) {return null;}
  const t = d.getTime();
  return Number.isFinite(t) ? t : null;
}

function exploreBrushRangeAroundFocusMs(focusMs: number): { start_time: string; end_time: string } {
  return {
    start_time: new Date(focusMs - CTRL_SEEK_HALF_SPAN_MS).toISOString(),
    end_time: new Date(focusMs + CTRL_SEEK_HALF_SPAN_MS).toISOString(),
  };
}

/** Brush selection shape expected by map time-series (matches D3 brush handler). */
type ExploreBrushRange = {
  type: 'range';
  start_time: string;
  end_time: string;
};

function toExploreBrushRange(interval: { start_time: string; end_time: string }): ExploreBrushRange {
  return {
    type: 'range',
    start_time: interval.start_time,
    end_time: interval.end_time,
  };
}

function exploreBrushRangeFromFocusMs(focusMs: number): ExploreBrushRange {
  return toExploreBrushRange(exploreBrushRangeAroundFocusMs(focusMs));
}

function userEventTypeUsesIntervalHighlight(et: string): boolean {
  const u = et.trim().toUpperCase();
  return u === 'TEST' || u === 'SELECTION' || u === 'PRESTART' || u === 'RACE';
}

function userEventIntervalRange(
  row: ExploreUserEventRow,
  timezone?: string | null
): { start_time: string; end_time: string } | null {
  const st = normalizeIsoForTimezone(row.start_time, timezone);
  const en = normalizeIsoForTimezone(row.end_time, timezone);
  if (!st || !en) {
    return null;
  }
  return { start_time: st, end_time: en };
}

/** Dataset PRESTART: seek to end_time (race start). User PRESTART uses focus via seekToUserEvent. */
function datasetEventSeekIso(ev: TagsDatasetEvent): string | null {
  const et = (ev.event_type || '').trim().toUpperCase();
  if (et === 'PRESTART') {
    const iso = ev.end_time?.trim() || ev.start_time?.trim();
    return iso || null;
  }
  const iso = ev.start_time?.trim();
  return iso || null;
}

function userEventSortMs(row: ExploreUserEventRow, timezone?: string | null): number {
  const et = (row.event_type || '').trim().toUpperCase();
  if (userEventTypeUsesIntervalHighlight(et)) {
    return (
      parseTimeMs(row.start_time, timezone) ||
      parseTimeMs(row.focus_time, timezone) ||
      parseTimeMs(row.date, timezone)
    );
  }
  return (
    parseTimeMs(row.focus_time, timezone) ||
    parseTimeMs(row.start_time, timezone) ||
    parseTimeMs(row.date, timezone)
  );
}

const MANEUVER_TYPE_SET = new Set(['tack', 'gybe', 'roundup', 'bearaway']);
/** Dataset events shown in Tags rail under System (matches /api/user-events/explore UNION). */
const SYSTEM_TAG_DATASET_TYPE_SET = new Set([
  ...MANEUVER_TYPE_SET,
  'race',
  'leg',
  'prestart',
]);
/** Matches Maneuvers bulk tag updates; server updateEventTags uses event_type = ANY($1) at maneuver mid_time. */
const MANEUVER_EVENT_TYPES_FOR_TAGS_API: readonly string[] = ['TACK', 'GYBE', 'BEARAWAY', 'ROUNDUP'];
type TagsDatasetEvent = DatasetEvent & { focus_time?: string | null };

const DATASET_DISPLAY_USER = 'System';

type ShortMediaTagPayload = {
  id: string;
  mediaSource: string;
  start: Date;
  end: Date;
};

type MergedRow =
  | { kind: 'user_event'; sortMs: number; user: ExploreUserEventRow }
  | { kind: 'dataset_event'; sortMs: number; ev: TagsDatasetEvent }
  | { kind: 'media'; sortMs: number; media: ShortMediaTagPayload };

type EditTarget =
  | { kind: 'user_event'; user: ExploreUserEventRow }
  | { kind: 'dataset_event'; ev: TagsDatasetEvent }
  | { kind: 'media'; media: ShortMediaTagPayload };

type DisplayRowEntry = { item: MergedRow; isGroupStart: boolean };

function parseTimeMs(iso: string | null | undefined, timezone?: string | null): number {
  const d = parseUtcToDate(iso) ?? parseIsoToDateWithTimezone(iso, timezone);
  if (!d) {return 0;}
  const t = d.getTime();
  return Number.isFinite(t) ? t : 0;
}

function fmtTimeOnly(iso: string | null | undefined, timezone?: string | null): string {
  const d = parseUtcToDate(iso) ?? parseIsoToDateWithTimezone(iso, timezone);
  if (!d) {return '—';}
  return formatTime(d, timezone) ?? '—';
}

function bubbleClassForEventType(eventType: string): string {
  const x = (eventType || '').toLowerCase();
  if (x.includes('comment')) {return 'tagger-msg__bubble--comment';}
  if (x.includes('prestart')) {return 'tagger-msg__bubble--prestart';}
  if (x === 'race') {return 'tagger-msg__bubble--race';}
  if (x.includes('test')) {return 'tagger-msg__bubble--test';}
  if (x.includes('selection')) {return 'tagger-msg__bubble--test';}
  if (x.includes('sail')) {return 'tagger-msg__bubble--sail';}
  if (x.includes('crew')) {return 'tagger-msg__bubble--crew';}
  if (x === 'job' || x.includes('job')) {return 'tagger-msg__bubble--job';}
  if (x === 'flag' || x.includes('flag')) {return 'tagger-msg__bubble--flag';}
  if (x === 'nice' || x.includes('nice')) {return 'tagger-msg__bubble--nice';}
  if (x.includes('good balance') || x.includes('good_balance')) {return 'tagger-msg__bubble--good-balance';}
  if (x.includes('bad moment') || x.includes('bad_moment')) {return 'tagger-msg__bubble--bad-moment';}
  if (x === 'media') {return 'tagger-msg__bubble--media';}
  return 'tagger-msg__bubble--default';
}

function userEventBodyText(row: ExploreUserEventRow): string {
  const c = (row.comment || '').trim();
  if (c) {return c;}
  const et = (row.event_type || '').toUpperCase();
  if (et === 'TEST' || et === 'SELECTION') {return et === 'TEST' ? 'Test' : 'Selection';}
  return row.event_type || 'Event';
}

function crewTagListText(row: ExploreUserEventRow): string | null {
  if ((row.event_type || '').trim().toUpperCase() !== 'CREW') {return null;}
  const summary = formatCrewTagSummary(row.tags ?? row.comment);
  return summary || null;
}

function userEventPrimaryLine(row: ExploreUserEventRow): string {
  const crew = crewTagListText(row);
  if (crew) {return crew;}
  const raw = (row.comment || '').trim();
  const et = (row.event_type || '').toUpperCase();
  if (raw) {
    if (et === 'TEST') {return 'Test';}
    if (et === 'SELECTION') {return 'Selection';}
    return row.event_type || 'Event';
  }
  return userEventBodyText(row);
}

function userEventCommentSubline(row: ExploreUserEventRow): string | null {
  if ((row.event_type || '').trim().toUpperCase() === 'CREW') {return null;}
  const raw = (row.comment || '').trim();
  return raw ? raw : null;
}

function mergeNoteIntoTagsJson(
  existing: string | Record<string, unknown> | null | undefined,
  note: string
): string {
  const base = parseDatasetEventTags(existing) ?? {};
  return JSON.stringify({ ...base, NOTE: note });
}

function mergeThumbsIntoTagsJson(
  existing: string | Record<string, unknown> | null | undefined,
  v: boolean | null
): string {
  const o = { ...(parseDatasetEventTags(existing) ?? {}) };
  if (v === null) {
    delete o.thumbsup;
  } else {
    o.thumbsup = v;
  }
  return JSON.stringify(o);
}

function isManeuverDatasetEvent(ev: DatasetEvent): boolean {
  return MANEUVER_TYPE_SET.has((ev.event_type || '').toLowerCase());
}

function isSystemTagDatasetEvent(ev: TagsDatasetEvent): boolean {
  return SYSTEM_TAG_DATASET_TYPE_SET.has((ev.event_type || '').toLowerCase());
}

/** Sort/coverage instant for dataset maneuvers (matches explore API focus_time). */
function datasetEventSortMs(ev: TagsDatasetEvent, timezone?: string | null): number {
  return (
    parseTimeMs(ev.focus_time, timezone) ||
    parseTimeMs(ev.start_time, timezone) ||
    parseTimeMs(ev.end_time, timezone)
  );
}

function eventTypesForDatasetTagPut(ev: DatasetEvent): string[] {
  if (isManeuverDatasetEvent(ev)) {
    return [...MANEUVER_EVENT_TYPES_FOR_TAGS_API];
  }
  const t = String(ev.event_type || '').trim().toUpperCase();
  return t ? [t] : [...MANEUVER_EVENT_TYPES_FOR_TAGS_API];
}

function rowDisplayUserName(item: MergedRow): string {
  if (item.kind === 'user_event') {
    const n = (item.user.user_name || '').trim();
    return n || 'Unknown';
  }
  return DATASET_DISPLAY_USER;
}

function rowEventTypeLabel(item: MergedRow): string {
  if (item.kind === 'user_event') {return item.user.event_type;}
  if (item.kind === 'media') {return 'MEDIA';}
  return item.ev.event_type;
}

function authorGroupKeyForMergedRow(item: MergedRow): string {
  if (item.kind === 'user_event') {
    const n = (item.user.user_name || '').trim().toLowerCase();
    return n ? `n:${n}` : 'n:unknown';
  }
  if (item.kind === 'media') {return 'm:media';}
  return 'sys';
}

function mergedRowToUserEvent(row: ExploreTagMergedRow, timezone?: string | null): ExploreUserEventRow {
  const focus_time = canonicalExploreTimeField(row.focus_time_utc, row.focus_time, timezone);
  const start_time = canonicalExploreTimeField(row.start_time_utc, row.start_time, timezone);
  const end_time = canonicalExploreTimeField(row.end_time_utc, row.end_time, timezone);
  return {
    user_event_id: row.user_event_id ?? 0,
    project_id: row.project_id,
    user_id: null,
    user_name: row.user_name ?? null,
    date: row.date ?? null,
    focus_time,
    start_time,
    end_time,
    focus_time_utc: focus_time,
    start_time_utc: start_time,
    end_time_utc: end_time,
    event_type: row.event_type,
    comment: row.comment || '',
    tags: parseDatasetEventTags(row.tags) ?? null,
  };
}

function mergedRowToDatasetEvent(row: ExploreTagMergedRow, timezone?: string | null): TagsDatasetEvent {
  const start_time = canonicalExploreTimeField(row.start_time_utc, row.start_time, timezone) ?? '';
  const end_time = canonicalExploreTimeField(row.end_time_utc, row.end_time, timezone) ?? '';
  const focus_time = canonicalExploreTimeField(row.focus_time_utc, row.focus_time, timezone);
  const tags =
    row.tags == null
      ? null
      : typeof row.tags === 'string'
        ? row.tags
        : JSON.stringify(row.tags);
  return {
    event_id: row.event_id ?? 0,
    event_type: row.event_type,
    start_time,
    end_time,
    focus_time,
    tags,
  };
}

export default function TagsExplorePanel() {
  const {
    selectedProjectId,
    selectedClassName,
    selectedDatasetId,
    selectedDate,
    selectedSourceId,
    selectedSourceName,
    setSelectedSourceId,
    setSelectedSourceName,
    tagsExploreRailOpen,
  } = persistantStore;

  const [rows, setRows] = createSignal<MergedRow[]>([]);
  const [loading, setLoading] = createSignal(false);
  const [errorMsg, setErrorMsg] = createSignal<string | null>(null);

  const [settingsOpen, setSettingsOpen] = createSignal(false);
  const [searchTerm, setSearchTerm] = createSignal('');
  const [selectedUser, setSelectedUser] = createSignal('all');
  const [selectedEventType, setSelectedEventType] = createSignal('all');
  /** When false (default), list hides tags outside continuous map telemetry (gap-aware). When true, list shows all loaded tags. */
  const [showAllTags, setShowAllTags] = createSignal(false);
  /** When true, hover reveals Edit on tags and opens the full edit modal (focus time + note). */
  const [editModeEnabled, setEditModeEnabled] = createSignal(false);
  /** Map cache is not reactive; bump to re-read segments (interval + key deps). */
  const [coverageRefreshNonce, setCoverageRefreshNonce] = createSignal(0);

  const [editTarget, setEditTarget] = createSignal<EditTarget | null>(null);
  const [editText, setEditText] = createSignal('');
  const [editFocusTimeLocal, setEditFocusTimeLocal] = createSignal('');
  const [editStartTimeLocal, setEditStartTimeLocal] = createSignal('');
  const [editEndTimeLocal, setEditEndTimeLocal] = createSignal('');
  const [editRaceNumberLocal, setEditRaceNumberLocal] = createSignal('');
  const [crewEditValues, setCrewEditValues] = createSignal<CrewTagEditValues>(emptyCrewTagEditValues());
  const [editErrorMsg, setEditErrorMsg] = createSignal<string | null>(null);
  const [editSaving, setEditSaving] = createSignal(false);
  /** Staged thumbs in edit modal only; persisted on Save, discarded on Cancel. */
  const [editModalDraftThumbs, setEditModalDraftThumbs] = createSignal<boolean | null>(null);
  /** Bumped when explore map (or other UI) creates user events so the list refetches. */
  const [exploreUserEventsRefreshNonce, setExploreUserEventsRefreshNonce] = createSignal(0);
  const [tagsTimezone, setTagsTimezone] = createSignal<string | null>(null);

  createEffect(() => {
    const className = selectedClassName();
    const projectId = selectedProjectId();
    const datasetId = selectedDatasetId();
    const dateRaw = (selectedDate() || '').trim();
    const ymd = dateRaw.replace(/[-/]/g, '');
    let cancelled = false;
    const run = async () => {
      if (!className || !projectId || projectId <= 0) {
        setTagsTimezone(null);
        return;
      }
      let tz: string | null = null;
      if (/^\d{8}$/.test(ymd)) {
        tz = await getTimezoneForDate(className, projectId, ymd);
      }
      if (!tz && datasetId > 0) {
        tz = await fetchDatasetTimezone(className, projectId, datasetId);
      }
      if (!cancelled) {
        setTagsTimezone(tz || null);
      }
    };
    void run();
    onCleanup(() => {
      cancelled = true;
    });
  });

  /** Raw map rows from unifiedDataStore cache (same keys as explore map / MapTimeSeries). */
  const telemetryMapPoints = createMemo((): unknown[] | null => {
    void coverageRefreshNonce();
    const cn = selectedClassName();
    const pid = selectedProjectId();
    if (!cn || !pid || pid <= 0) {return null;}
    const dsId = selectedDatasetId();
    if (dsId > 0) {
      const sid = selectedSourceId();
      if (!Number.isFinite(sid) || sid <= 0) {return null;}
      return unifiedDataStore.getMapDataForDatasetFromCache(cn, pid, dsId, sid);
    }
    const dayRaw = (selectedDate() || '').trim();
    const ymd = dayRaw.replace(/[-/]/g, '').slice(0, 8);
    if (!/^\d{8}$/.test(ymd)) {return null;}
    const sid = selectedSourceId();
    const sourceSet = Number.isFinite(sid) && sid > 0 ? new Set<number>([sid]) : undefined;
    return unifiedDataStore.getMapDataForDayFromCache(cn, pid, ymd, sourceSet);
  });

  /** Fleet map full-mode bounds from race/leg event intervals (preferred) or filtered telemetry. */
  const fleetMapFilteredTimeBounds = createMemo((): { startMs: number; endMs: number } | null => {
    void coverageRefreshNonce();
    void rows();
    if (isTrainingHourMode()) {return null;}
    const races = selectedRacesTimeseries().map(String);
    const legs = selectedLegsTimeseries().map(String);
    if (races.length === 0 && legs.length === 0) {return null;}

    const tagRowBounds = getFleetRaceLegBoundsFromTagRows(
      rows()
        .filter((r): r is Extract<MergedRow, { kind: 'dataset_event' }> => r.kind === 'dataset_event')
        .map((r) => ({
          kind: 'dataset_event',
          event_type: r.ev.event_type,
          start_time: r.ev.start_time || null,
          end_time: r.ev.end_time || null,
          tags: r.ev.tags,
        })),
      races,
      legs
    );
    if (tagRowBounds) {return tagRowBounds;}

    const pts = telemetryMapPoints();
    if (pts == null || pts.length === 0) {return null;}
    return getFleetRaceLegFilteredBoundsMs(pts, {
      races,
      legs,
      trainingHourMode: false,
    });
  });

  /** Cut ∩ brush, or map playback time window when the tags rail is open (same window as visible track). */
  const exploreVisibleTimeBounds = createMemo(() => {
    void tagsExploreRailOpen();
    void timeWindow();
    void selectedTime();
    void selectedRacesTimeseries();
    void selectedLegsTimeseries();
    void isTrainingHourMode();
    void coverageRefreshNonce();
    if (isPlaying()) {void smoothPlaybackTimeForTrack();}
    return getExploreVisibleBoundsIncludingPlaybackMs({
      applyCut: isCut(),
      cutEvents: cutEvents(),
      brushRanges: selectedRange(),
      applyPlaybackWindow: tagsExploreRailOpen() && shouldApplyPlaybackTimeWindow(),
      windowMinutes: timeWindow(),
      referenceTime: getDisplayWindowReferenceTime() ?? selectedTime(),
      fleetFilteredBounds: fleetMapFilteredTimeBounds(),
    });
  });

  /** Selected boat name, from the session or the source id when the name is still empty. */
  const selectedBoatName = createMemo(() => {
    const named = selectedSourceName().trim();
    if (named) {return named;}
    const sid = selectedSourceId();
    if (!Number.isFinite(sid) || sid <= 0) {return '';}
    const match = sourcesStore.sources().find((s) => Number(s?.source_id) === Number(sid));
    return String(match?.source_name ?? '').trim();
  });

  /** Crew tags are GER-only until each event's boat list can be trusted. */
  const rowsForBoat = createMemo(() => {
    const boat = selectedBoatName();
    return rows().filter((item) => crewTagVisibleForSource(rowEventTypeLabel(item), boat));
  });

  /** Loaded rows restricted to cut, brush, and/or playback window; full list when none apply. */
  const rowsVisibleForCut = createMemo(() => {
    const r = rowsForBoat();
    const b = exploreVisibleTimeBounds();
    if (b.kind === 'empty') {return [];}
    if (b.kind === 'none') {return r;}
    return r.filter((row) => row.sortMs >= b.startMs && row.sortMs <= b.endMs);
  });

  /** null = no cached points → fall back to timeline-only filter. */
  const telemetryCoverageSegments = createMemo(() => {
    void coverageRefreshNonce();
    const pts = telemetryMapPoints();
    if (pts == null || pts.length === 0) {return null;}
    const sorted = extractSortedTimestampsMs(pts);
    if (sorted.length === 0) {return null;}
    return buildCoverageSegmentsMs(sorted, MAP_TRACK_GAP_THRESHOLD_MS);
  });

  /** Timeline window ∩ gap-aware track coverage when segments exist; else timeline only. */
  const rowsVisibleForData = createMemo(() => {
    const base = rowsVisibleForCut();
    const segs = telemetryCoverageSegments();
    const hasFleetEventWindow = fleetMapFilteredTimeBounds() != null;
    if (segs == null || segs.length === 0) {return base;}
    return base.filter((row) => {
      // Maneuvers stay visible across telemetry gaps only when not scoped to a race/leg event window.
      if (row.kind === 'dataset_event' && !hasFleetEventWindow) {return true;}
      return isMsInsideSegments(row.sortMs, segs);
    });
  });

  /** Strict map-marker source: active time window + selected map telemetry segments. */
  const rowsVisibleForSelectedMapData = createMemo(() => {
    const b = exploreVisibleTimeBounds();
    if (b.kind !== 'window') {return [];}
    const segs = telemetryCoverageSegments();
    if (segs == null || segs.length === 0) {return [];}
    return rowsVisibleForCut().filter((row) => isMsInsideSegments(row.sortMs, segs));
  });

  const rowsForListDisplay = createMemo(() => {
    // Always return a shallow copy so toggling showAllTags still updates downstream memos when
    // rows() and rowsVisibleForData() would otherwise be the same reference (e.g. full timeline, no gap filter).
    // "Show all" skips gap coverage and playback time-window filters (list only; map markers stay windowed).
    if (showAllTags()) {return rowsForBoat().slice();}
    return rowsVisibleForData().slice();
  });

  let prevTagsLoading = false;
  createEffect(() => {
    const l = loading();
    if (prevTagsLoading && !l) {
      setCoverageRefreshNonce((n) => n + 1);
    }
    prevTagsLoading = l;
  });

  createEffect(() => {
    const open = tagsExploreRailOpen();
    if (!open || showAllTags()) {return;}
    const id = window.setInterval(() => setCoverageRefreshNonce((n) => n + 1), 2000);
    onCleanup(() => window.clearInterval(id));
  });

  createEffect(() => {
    if (!tagsExploreRailOpen() || showAllTags()) {return;}
    void selectedRange();
    void isCut();
    void cutEvents();
    void tagsExploreRailOpen();
    void timeWindow();
    void selectedTime();
    if (isPlaying()) {void smoothPlaybackTimeForTrack();}
    void selectedDatasetId();
    void selectedSourceId();
    void selectedDate();
    void selectedProjectId();
    void selectedClassName();
    void selectedRacesTimeseries();
    void selectedLegsTimeseries();
    void isTrainingHourMode();
    void rows();
    setCoverageRefreshNonce((n) => n + 1);
  });

  const tagsListEmptyReason = createMemo((): 'none' | 'time' | 'coverage' | 'filters' => {
    if (loading() || errorMsg()) {return 'none';}
    if (rows().length === 0) {return 'none';}
    if (rowsForListDisplay().length > 0 && displayRows().length === 0) {return 'filters';}
    if (rowsForListDisplay().length > 0) {return 'none';}
    if (rowsVisibleForCut().length === 0) {return 'time';}
    const segs = telemetryCoverageSegments();
    if (segs != null && segs.length > 0) {return 'coverage';}
    return 'time';
  });

  let clickTimer: number | null = null;
  let lastTagClickKey: string | null = null;
  onCleanup(() => {
    if (clickTimer != null) {
      window.clearTimeout(clickTimer);
      clickTimer = null;
    }
    lastTagClickKey = null;
    setTagsExploreMapMarkers([]);
  });

  createEffect(() => {
    if (!tagsExploreRailOpen()) {
      setTagsExploreMapMarkers([]);
      return;
    }
    // Markers are only drawn where selected map telemetry exists; "Show all" affects the list only.
    const list = rowsVisibleForSelectedMapData();
    const inputs = list.map((r) => {
      if (r.kind === 'user_event') {return { kind: 'user_event' as const, user: r.user };}
      if (r.kind === 'media') {
        return {
          kind: 'media' as const,
          id: r.media.id,
          mediaSource: r.media.mediaSource,
          start: r.media.start,
          end: r.media.end,
        };
      }
      return { kind: 'dataset_event' as const, ev: r.ev };
    });
    setTagsExploreMapMarkers(buildTagExploreMapMarkers(inputs));
  });

  createEffect(() => {
    const tgt = editTarget();
    if (!tgt) {
      setEditModalDraftThumbs(null);
      setEditErrorMsg(null);
      setEditFocusTimeLocal('');
      setEditStartTimeLocal('');
      setEditEndTimeLocal('');
      setEditRaceNumberLocal('');
      return;
    }
    if (tgt.kind === 'media') {
      setEditModalDraftThumbs(null);
      return;
    }
    const v =
      tgt.kind === 'user_event'
        ? tagThumbsFromTags(tgt.user.tags as Record<string, unknown> | null)
        : datasetThumbsState(tgt.ev);
    setEditModalDraftThumbs(v);
  });

  const editTargetEventTypeUpper = createMemo(() => {
    const tgt = editTarget();
    if (!tgt || tgt.kind === 'media') {return '';}
    if (tgt.kind === 'user_event') {return (tgt.user.event_type || '').trim().toUpperCase();}
    return (tgt.ev.event_type || '').trim().toUpperCase();
  });

  const editTargetShowsCommentField = createMemo(() => {
    const tgt = editTarget();
    if (!tgt || tgt.kind === 'media') {return false;}
    const et = editTargetEventTypeUpper();
    if (et === 'RACE' || et === 'PRESTART') {return false;}
    if (tgt.kind === 'dataset_event') {return true;}
    return taggerUserEventShowsCommentField(tgt.user.event_type || '');
  });

  const editTargetShowsTimeFields = createMemo(() => {
    const tgt = editTarget();
    if (!tgt || tgt.kind === 'media') {return false;}
    if (tgt.kind === 'user_event') {return true;}
    const et = (tgt.ev.event_type || '').trim().toUpperCase();
    return et === 'RACE' || et === 'PRESTART';
  });

  const editTargetStartOnly = createMemo(() => {
    const et = editTargetEventTypeUpper();
    return et === 'RACE' || et === 'PRESTART';
  });

  const editTargetShowsThumbs = createMemo(() => {
    const tgt = editTarget();
    return tgt?.kind === 'dataset_event' && isManeuverDatasetEvent(tgt.ev);
  });

  const editTargetShowsCrewFields = createMemo(() => editTargetEventTypeUpper() === 'CREW');

  const setCrewEditField = (key: CrewTagEditField, value: string) => {
    setCrewEditValues((prev) => ({ ...prev, [key]: value }));
  };

  const resetUserEventEditTimeFields = (row: ExploreUserEventRow) => {
    const etUpper = (row.event_type || '').trim().toUpperCase();
    const prestartTimeIso = taggerEventTypeIsPrestart(etUpper)
      ? prestartTimeIsoFromExploreRow(row)
      : row.focus_time?.trim() || '';
    setEditFocusTimeLocal(
      taggerEventTypeIsPrestart(etUpper)
        ? isoToDatetimeLocalValue(prestartTimeIso)
        : isoToDatetimeLocalValue(row.focus_time)
    );
    setEditStartTimeLocal(isoToDatetimeLocalValue(row.start_time));
    setEditEndTimeLocal(row.end_time ? isoToDatetimeLocalValue(row.end_time) : '');
    const rn = raceNumberFromExploreTags(row.tags as Record<string, unknown> | null, row.event_type);
    setEditRaceNumberLocal(rn != null ? String(rn) : '');
  };

  const uniqueUsers = createMemo(() => {
    const names = new Set<string>();
    for (const r of rowsForListDisplay()) {
      const n = rowDisplayUserName(r);
      if (n) {names.add(n);}
    }
    return [...names].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  });

  const uniqueEventTypes = createMemo(() => {
    const types = new Set<string>();
    for (const r of rowsForListDisplay()) {
      const et = rowEventTypeLabel(r);
      if (et) {types.add(et);}
    }
    return [...types].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  });

  const displayRows = createMemo(() => {
    let list = rowsForListDisplay();
    const tz = tagsTimezone();
    const term = searchTerm().trim().toLowerCase();
        if (term) {
      list = list.filter((item) => {
        if (item.kind === 'user_event') {
          const u = item.user;
          const body = userEventBodyText(u).toLowerCase();
          const sub = (userEventCommentSubline(u) || '').toLowerCase();
          const un = (u.user_name || '').toLowerCase();
          const et = (u.event_type || '').toLowerCase();
          const time = fmtTimeOnly(u.focus_time ?? u.start_time, tz).toLowerCase();
          return (
            body.includes(term) ||
            sub.includes(term) ||
            un.includes(term) ||
            et.includes(term) ||
            time.includes(term)
          );
        }
        if (item.kind === 'media') {
          const m = item.media;
          const src = (m.mediaSource || '').toLowerCase();
          const time = fmtTimeOnly(m.start.toISOString(), tz).toLowerCase();
          const sys = DATASET_DISPLAY_USER.toLowerCase();
          return src.includes(term) || 'media'.includes(term) || time.includes(term) || sys.includes(term);
        }
        const ev = item.ev;
        const noteTrim = noteTextFromDatasetEvent(ev).trim();
        const noteLc = noteTrim.toLowerCase();
        const bodyLc = (noteTrim || ev.event_type || 'Event').toLowerCase();
        const et = (ev.event_type || '').toLowerCase();
        const time = fmtTimeOnly(ev.start_time, tz).toLowerCase();
        const sys = DATASET_DISPLAY_USER.toLowerCase();
        return (
          bodyLc.includes(term) ||
          noteLc.includes(term) ||
          et.includes(term) ||
          time.includes(term) ||
          sys.includes(term)
        );
      });
    }
    if (selectedUser() !== 'all') {
      list = list.filter((item) => rowDisplayUserName(item) === selectedUser());
    }
    if (selectedEventType() !== 'all') {
      list = list.filter((item) => rowEventTypeLabel(item) === selectedEventType());
    }
    return list;
  });

  const tagsExploreAuthorOrdinalMap = createMemo(() =>
    buildTaggerAuthorOrdinalMap(displayRows().map((item) => rowDisplayUserName(item)))
  );

  const displayRowsWithGroups = createMemo((): DisplayRowEntry[] => {
    const list = displayRows();
    let prev: string | null = null;
    return list.map((item) => {
      const key = authorGroupKeyForMergedRow(item);
      const isGroupStart = prev === null || prev !== key;
      prev = key;
      return { item, isGroupStart };
    });
  });

  createEffect(() => {
    if (!editModeEnabled() && editTarget()) {
      setEditTarget(null);
    }
  });

  createEffect(() => {
    const bump = () => setExploreUserEventsRefreshNonce((n) => n + 1);
    window.addEventListener(EXPLORE_USER_EVENTS_CHANGED_EVENT, bump);
    onCleanup(() => window.removeEventListener(EXPLORE_USER_EVENTS_CHANGED_EVENT, bump));
  });

  createEffect(() => {
    void exploreUserEventsRefreshNonce();
    const projectId = selectedProjectId();
    const className = selectedClassName();
    const datasetId = selectedDatasetId();
    const dateStr = selectedDate();
    const ac = new AbortController();
    let cancelled = false;
    const timezone = tagsTimezone();

    const run = async () => {
      setErrorMsg(null);
      if (!projectId || projectId <= 0 || !className) {
        setRows([]);
        return;
      }

      const isDataset = datasetId > 0;
      const targetYmd = normalizeExploreTargetDate(dateStr);
      if (!isDataset && !targetYmd) {
        setRows([]);
        setErrorMsg('Select a calendar day to load tags');
        return;
      }

      setLoading(true);
      try {
        const apiRows = await fetchExploreTagRows({
          className,
          projectId,
          datasetId: isDataset ? datasetId : null,
          targetYmd: isDataset ? null : targetYmd,
          timezone,
          signal: ac.signal,
        });
        if (cancelled) {return;}

        const merged: MergedRow[] = [];
        let apiUser = 0;
        let apiDataset = 0;
        let skippedNonSystemTag = 0;
        for (const row of apiRows) {
          const kind = exploreRowKind(row);
          if (kind === 'user_event') {apiUser += 1;}
          if (kind === 'dataset_event') {apiDataset += 1;}
          if (kind === 'user_event' && row.user_event_id != null) {
            const user = mergedRowToUserEvent(row, timezone);
            merged.push({ kind: 'user_event', sortMs: userEventSortMs(user, timezone), user });
            continue;
          }
          if (kind === 'dataset_event' && row.event_id != null) {
            const ev = mergedRowToDatasetEvent(row, timezone);
            if (!isSystemTagDatasetEvent(ev)) {
              skippedNonSystemTag += 1;
              continue;
            }
            merged.push({ kind: 'dataset_event', sortMs: datasetEventSortMs(ev, timezone), ev });
          }
        }
        if (apiDataset > 0 && merged.filter((r) => r.kind === 'dataset_event').length === 0) {
          warn('[TagsExplorePanel] API returned dataset_event rows but none were merged', {
            apiDataset,
            skippedNonSystemTag,
            sampleTypes: apiRows
              .filter((r) => exploreRowKind(r) === 'dataset_event')
              .slice(0, 5)
              .map((r) => r.event_type),
          });
        }

        const calendarForMedia = (dateStr || '').trim().slice(0, 10);
        const shortMediaRows: MergedRow[] = [];
        if (!cancelled && className && /^\d{4}-\d{2}-\d{2}$/.test(calendarForMedia)) {
          try {
            const wins = await mediaAvailabilityService.getWindowsForDate(calendarForMedia, timezone ?? undefined);
            if (!cancelled) {
              for (const w of wins) {
                const dur = w.end.getTime() - w.start.getTime();
                if (dur >= SHORT_MEDIA_MAX_DURATION_MS) {continue;}
                const srcKey = (w.sourceName || w.sourceId || '').trim();
                if (srcKey.toLowerCase().includes('_cam')) {continue;}
                shortMediaRows.push({
                  kind: 'media',
                  sortMs: w.start.getTime(),
                  media: {
                    id: w.id ?? '',
                    mediaSource: srcKey || '—',
                    start: w.start instanceof Date ? w.start : new Date(w.start),
                    end: w.end instanceof Date ? w.end : new Date(w.end),
                  },
                });
              }
              debug('[TagsExplorePanel] short media tags merged', {
                count: shortMediaRows.length,
                date: calendarForMedia,
              });
            }
          } catch (e) {
            logError('[TagsExplorePanel] short media load error', e);
          }
        } else if (!cancelled && className) {
          debug('[TagsExplorePanel] Skipping short media fetch: no valid calendar date', {
            dateStr: dateStr ?? null,
          });
        }

        const combined = [...merged, ...shortMediaRows].sort((a, b) => a.sortMs - b.sortMs);
        setRows(combined);
        debug('[TagsExplorePanel] loaded merged rows', {
          count: combined.length,
          apiUser,
          apiDataset,
          mergedUser: merged.filter((r) => r.kind === 'user_event').length,
          mergedDataset: merged.filter((r) => r.kind === 'dataset_event').length,
          targetYmd: isDataset ? null : targetYmd,
          datasetId: isDataset ? datasetId : null,
          isDataset,
        });
      } catch (e) {
        logError('[TagsExplorePanel] load error', e);
        setRows([]);
        setErrorMsg('Failed to load tags');
      } finally {
        if (!cancelled) {setLoading(false);}
      }
    };

    void run();
    onCleanup(() => {
      ac.abort();
      cancelled = true;
    });
  });

  const sourceNameById = (sid: number): string | null => {
    const sources = sourcesStore.sources();
    const match = sources.find((s) => Number(s?.source_id) === Number(sid));
    const name = match?.source_name;
    return typeof name === 'string' && name.trim() ? name.trim() : null;
  };

  const sourceIdByName = (name: string): number | null => {
    const needle = String(name || '').trim().toLowerCase();
    if (!needle) {return null;}
    const sources = sourcesStore.sources();
    const match = sources.find((s) => String(s?.source_name || '').trim().toLowerCase() === needle);
    const sid = Number(match?.source_id);
    return Number.isFinite(sid) && sid > 0 ? sid : null;
  };

  const inferNearestTelemetrySource = (focusMs: number): { sourceId: number | null; sourceName: string | null } => {
    const points = telemetryMapPoints();
    if (!Array.isArray(points) || points.length === 0) {
      return { sourceId: null, sourceName: null };
    }
    const sources = sourcesStore.sources();
    let best: { sourceId: number | null; sourceName: string | null; delta: number } | null = null;
    for (const p of points as Array<Record<string, unknown>>) {
      const tRaw = p?.Datetime;
      const tMs = tRaw instanceof Date ? tRaw.getTime() : new Date(String(tRaw || '')).getTime();
      if (!Number.isFinite(tMs)) {continue;}
      const sid = extractRowSourceId(p, sources);
      const sname = extractRowSourceName(p) || (sid != null ? sourceNameById(sid) : null);
      if (sid == null && !sname) {continue;}
      const delta = Math.abs(tMs - focusMs);
      if (!best || delta < best.delta) {
        best = {
          sourceId: sid ?? null,
          sourceName: sname ?? null,
          delta,
        };
      }
    }
    if (!best) {
      return { sourceId: null, sourceName: null };
    }
    return { sourceId: best.sourceId, sourceName: best.sourceName };
  };

  const primeTagsSeekMapCenter = (opts?: { focusMs?: number | null; sourceName?: string | null }) => {
    const sid = selectedSourceId();
    if (sid > 0) {
      setTagsSeekMapCenter({ sourceId: sid });
      return;
    }
    const name = opts?.sourceName?.trim();
    if (name) {
      setTagsSeekMapCenter({ sourceId: sourceIdByName(name) });
      return;
    }
    if (Number.isFinite(Number(opts?.focusMs))) {
      const inferred = inferNearestTelemetrySource(Number(opts!.focusMs));
      setTagsSeekMapCenter({ sourceId: inferred.sourceId });
      return;
    }
    setTagsSeekMapCenter();
  };

  const seekToUserEvent = (row: ExploreUserEventRow) => {
    const iso = row.focus_time || row.start_time;
    if (!iso) {return;}
    const d = parseUtcToDate(iso) ?? parseIsoToDateWithTimezone(iso, tagsTimezone());
    if (!d) {return;}
    if (!requestTimeControl('tags_explore')) {return;}
    primeTagsSeekMapCenter({ focusMs: d.getTime() });
    setIsManualTimeChange(true);
    setSelectedTime(d, 'tags_explore');
    setTimeout(() => releaseTimeControl('tags_explore'), 100);
  };

  const seekToDatasetEvent = (ev: TagsDatasetEvent) => {
    const iso = datasetEventSeekIso(ev);
    if (!iso) {return;}
    const d = parseUtcToDate(iso) ?? parseIsoToDateWithTimezone(iso, tagsTimezone());
    if (!d) {return;}
    if (!requestTimeControl('tags_explore')) {return;}
    primeTagsSeekMapCenter({ focusMs: d.getTime() });
    setIsManualTimeChange(true);
    setSelectedTime(d, 'tags_explore');
    setTimeout(() => releaseTimeControl('tags_explore'), 100);
  };

  const seekToMedia = (m: ShortMediaTagPayload) => {
    const d = new Date(m.start.getTime());
    if (Number.isNaN(d.getTime())) {return;}
    if (!requestTimeControl('tags_explore')) {return;}
    primeTagsSeekMapCenter({ focusMs: d.getTime(), sourceName: m.mediaSource || null });
    setIsManualTimeChange(true);
    setSelectedTime(d, 'tags_explore');
    setTimeout(() => releaseTimeControl('tags_explore'), 100);
  };

  const applySourceFocus = (opts: { focusMs?: number | null; sourceName?: string | null; sourceId?: number | null }) => {
    let focusedSourceId: number | null = Number.isFinite(Number(opts.sourceId)) && Number(opts.sourceId) > 0
      ? Number(opts.sourceId)
      : null;
    let focusedSourceName: string | null = opts.sourceName && String(opts.sourceName).trim()
      ? String(opts.sourceName).trim()
      : null;

    if (focusedSourceId == null && focusedSourceName) {
      focusedSourceId = sourceIdByName(focusedSourceName);
    }
    if (!focusedSourceName && focusedSourceId != null) {
      focusedSourceName = sourceNameById(focusedSourceId);
    }

    if ((focusedSourceId == null || !focusedSourceName) && Number.isFinite(Number(opts.focusMs))) {
      const inferred = inferNearestTelemetrySource(Number(opts.focusMs));
      if (focusedSourceId == null) {focusedSourceId = inferred.sourceId;}
      if (!focusedSourceName) {focusedSourceName = inferred.sourceName;}
    }

    if (focusedSourceId != null) {
      setSelectedSourceId(focusedSourceId);
    }
    if (focusedSourceName) {
      setSelectedSourceName(focusedSourceName);
      // Fleet map source enablement is driven by filterStore selectedSources.
      setFilterStoreSelectedSources([focusedSourceName]);
    }
  };

  /** Ctrl/Cmd+click: store a timeline brush (not an event selection). */
  const applyExploreRangeBrush = (interval: { start_time: string; end_time: string }) => {
    batch(() => {
      setSelectedRanges([]);
      setSelectedEvents([], { defer: false });
      setSelectedRange([toExploreBrushRange(interval)]);
      setHasSelection(true);
      if (isCut()) {
        setIsCut(false);
      }
    });
  };

  /** Ctrl/Cmd+click on a point tag: ±30s brush around focus time. */
  const applyExploreFocusBrush = (focusMs: number) => {
    batch(() => {
      setSelectedRanges([]);
      setSelectedEvents([], { defer: false });
      setSelectedRange([exploreBrushRangeFromFocusMs(focusMs)]);
      setHasSelection(true);
      if (isCut()) {
        setIsCut(false);
      }
    });
  };

  const userEventRangeInterval = (
    row: ExploreUserEventRow
  ): { start_time: string; end_time: string } | null => {
    const interval = userEventIntervalRange(row, tagsTimezone());
    if (interval) {return interval;}
    const focusMs = focusTimeMsFromIso(row.focus_time ?? row.start_time, tagsTimezone());
    if (focusMs == null) {return null;}
    return {
      start_time: new Date(focusMs - EXPLORE_NOTE_FOCUS_PADDING_MS).toISOString(),
      end_time: new Date(focusMs + EXPLORE_NOTE_FOCUS_PADDING_MS).toISOString(),
    };
  };

  const datasetEventRangeInterval = (
    ev: DatasetEvent
  ): { start_time: string; end_time: string } | null => {
    const st = normalizeIsoForTimezone(ev.start_time, tagsTimezone());
    const en = normalizeIsoForTimezone(ev.end_time, tagsTimezone());
    if (st && en) {return { start_time: st, end_time: en };}
    const focusMs = focusTimeMsFromIso(datasetEventSeekIso(ev), tagsTimezone());
    if (focusMs == null) {return null;}
    return exploreBrushRangeAroundFocusMs(focusMs);
  };

  /** Double-click: highlight tag interval on map/timeline (all user-event types). */
  const applyUserEventRangeSelection = (row: ExploreUserEventRow) => {
    const interval = userEventRangeInterval(row);
    if (!interval) {
      seekToUserEvent(row);
      return;
    }
    batch(() => {
      setSelectedRange([toExploreBrushRange(interval)]);
      setSelectedEvents([], { defer: false });
      setSelectedRanges([
        {
          type: 'event',
          start_time: interval.start_time,
          end_time: interval.end_time,
          event_id: row.user_event_id,
        },
      ]);
      setHasSelection(true);
    });
    setTriggerSelection(true);
    applySourceFocus({
      focusMs: focusTimeMsFromIso(row.focus_time ?? row.start_time, tagsTimezone()),
    });
    seekToUserEvent(row);
  };

  /** Double-click: highlight maneuver / dataset-event interval on map/timeline. */
  const applyDatasetEventRangeSelection = (ev: DatasetEvent) => {
    const interval = datasetEventRangeInterval(ev);
    batch(() => {
      setSelectedRange(interval ? [toExploreBrushRange(interval)] : []);
      if (interval) {
        setSelectedRanges([
          {
            type: 'event',
            event_id: ev.event_id,
            start_time: interval.start_time,
            end_time: interval.end_time,
          },
        ]);
        setSelectedEvents([ev.event_id]);
        setHasSelection(true);
      } else {
        setSelectedRanges([]);
        setSelectedEvents([]);
        setHasSelection(false);
      }
    });
    setTriggerSelection(true);
    applySourceFocus({
      focusMs: focusTimeMsFromIso(datasetEventSeekIso(ev), tagsTimezone()),
    });
    seekToDatasetEvent(ev);
  };

  /** Double-click: highlight short media clip interval on map/timeline. */
  const applyMediaRangeSelection = (m: ShortMediaTagPayload) => {
    batch(() => {
      setSelectedRange([
        toExploreBrushRange({
          start_time: m.start.toISOString(),
          end_time: m.end.toISOString(),
        }),
      ]);
      setSelectedEvents([], { defer: false });
      setSelectedRanges([
        {
          type: 'event',
          start_time: m.start.toISOString(),
          end_time: m.end.toISOString(),
        },
      ]);
      setHasSelection(true);
    });
    setTriggerSelection(true);
    applySourceFocus({
      focusMs: m.start.getTime(),
      sourceName: m.mediaSource || null,
    });
    seekToMedia(m);
  };

  /** Single click seeks; double click on the same tag within {@link TAG_CLICK_DOUBLE_MS} selects range. */
  const runTagSingleOrDoubleClick = (clickKey: string, onSingle: () => void, onDouble: () => void) => {
    if (clickTimer != null && lastTagClickKey === clickKey) {
      window.clearTimeout(clickTimer);
      clickTimer = null;
      lastTagClickKey = null;
      onDouble();
      return;
    }
    if (clickTimer != null) {
      window.clearTimeout(clickTimer);
      clickTimer = null;
    }
    lastTagClickKey = clickKey;
    clickTimer = window.setTimeout(() => {
      clickTimer = null;
      lastTagClickKey = null;
      onSingle();
    }, TAG_CLICK_DOUBLE_MS);
  };

  const onUserRowClick = (row: ExploreUserEventRow, e: MouseEvent) => {
    if (isCtrlOrMetaClick(e)) {
      e.preventDefault();
      clearRowClickTimer();
      const et = (row.event_type || '').toUpperCase();
      const interval = userEventIntervalRange(row, tagsTimezone());
      if (et === 'PRESTART' || et === 'RACE') {
        if (interval == null) {return;}
        applyExploreRangeBrush(interval);
        queueMicrotask(() => seekToUserEvent(row));
        return;
      }
      const focusMs = focusTimeMsFromIso(row.focus_time ?? row.start_time, tagsTimezone());
      if (focusMs == null) {return;}
      applyExploreFocusBrush(focusMs);
      queueMicrotask(() => seekToUserEvent(row));
      return;
    }
    runTagSingleOrDoubleClick(
      `ue:${row.user_event_id}`,
      () => seekToUserEvent(row),
      () => applyUserEventRangeSelection(row)
    );
  };

  const clearRowClickTimer = () => {
    if (clickTimer != null) {
      window.clearTimeout(clickTimer);
      clickTimer = null;
    }
    lastTagClickKey = null;
  };

  const openUserEventEditor = (row: ExploreUserEventRow) => {
    clearRowClickTimer();
    setEditErrorMsg(null);
    setEditText(userEventBodyText(row));
    resetUserEventEditTimeFields(row);
    if ((row.event_type || '').trim().toUpperCase() === 'CREW') {
      setCrewEditValues(crewTagEditValuesFromTags(row.tags ?? row.comment));
    }
    setEditTarget({ kind: 'user_event', user: row });
  };

  const openMediaEditor = (media: ShortMediaTagPayload) => {
    clearRowClickTimer();
    setEditErrorMsg(null);
    setEditText('');
    setEditTarget({ kind: 'media', media });
  };

  const onDatasetRowClick = (ev: DatasetEvent, e: MouseEvent) => {
    if (isCtrlOrMetaClick(e)) {
      e.preventDefault();
      clearRowClickTimer();
      const et = (ev.event_type || '').trim().toUpperCase();
      const st = normalizeIsoForTimezone(ev.start_time, tagsTimezone());
      const en = normalizeIsoForTimezone(ev.end_time, tagsTimezone());
      if ((et === 'PRESTART' || et === 'RACE') && st && en) {
        applyExploreRangeBrush({ start_time: st, end_time: en });
        queueMicrotask(() => seekToDatasetEvent(ev));
        return;
      }
      const focusMs = focusTimeMsFromIso(datasetEventSeekIso(ev), tagsTimezone());
      if (focusMs == null) {return;}
      batch(() => {
        setSelectedRange([exploreBrushRangeAroundFocusMs(focusMs)]);
        if (st && en) {
          setSelectedRanges([
            {
              type: 'event',
              event_id: ev.event_id,
              start_time: st,
              end_time: en,
            },
          ]);
          setHasSelection(true);
        } else {
          setSelectedRanges([]);
          setHasSelection(true);
        }
        setSelectedEvents([ev.event_id]);
      });
      setTriggerSelection(true);
      queueMicrotask(() => seekToDatasetEvent(ev));
      return;
    }
    runTagSingleOrDoubleClick(
      `de:${ev.event_id}`,
      () => seekToDatasetEvent(ev),
      () => applyDatasetEventRangeSelection(ev)
    );
  };

  const onMediaRowClick = (m: ShortMediaTagPayload, e: MouseEvent) => {
    if (isCtrlOrMetaClick(e)) {
      e.preventDefault();
      clearRowClickTimer();
      const focusMs = m.start.getTime();
      if (!Number.isFinite(focusMs)) {return;}
      applyExploreFocusBrush(focusMs);
      queueMicrotask(() => seekToMedia(m));
      return;
    }
    runTagSingleOrDoubleClick(
      `media:${m.id}`,
      () => seekToMedia(m),
      () => applyMediaRangeSelection(m)
    );
  };

  const openDatasetEventEditor = (ev: DatasetEvent) => {
    clearRowClickTimer();
    setEditErrorMsg(null);
    setEditText(noteTextFromDatasetEvent(ev));
    const et = (ev.event_type || '').trim().toUpperCase();
    if (et === 'RACE' || et === 'PRESTART') {
      setEditStartTimeLocal(isoToDatetimeLocalValue(ev.start_time));
      setEditEndTimeLocal(ev.end_time ? isoToDatetimeLocalValue(ev.end_time) : '');
      const rn = raceTagNumber(parseDatasetEventTags(ev.tags), et);
      setEditRaceNumberLocal(rn != null ? String(rn) : '');
    }
    setEditTarget({ kind: 'dataset_event', ev });
  };

  const applyUserThumbs = async (row: ExploreUserEventRow, next: boolean | null, e: MouseEvent) => {
    e.stopPropagation();
    const projectId = selectedProjectId();
    const updated = await putUserEventExploreUpdate(projectId, row, { thumbsup: next });
    if (updated) {
      setRows((prev) =>
        prev.map((r) =>
          r.kind === 'user_event' && r.user.user_event_id === row.user_event_id ? { ...r, user: updated } : r
        )
      );
    }
  };

  const onUserThumbUp = (row: ExploreUserEventRow, e: MouseEvent) => {
    const cur = tagThumbsFromTags(row.tags as Record<string, unknown> | null);
    const next = cur === true ? null : true;
    void applyUserThumbs(row, next, e);
  };

  const onUserThumbDown = (row: ExploreUserEventRow, e: MouseEvent) => {
    const cur = tagThumbsFromTags(row.tags as Record<string, unknown> | null);
    const next = cur === false ? null : false;
    void applyUserThumbs(row, next, e);
  };

  const applyDatasetThumbs = async (ev: DatasetEvent, next: boolean | null, e: MouseEvent) => {
    e.stopPropagation();
    const className = selectedClassName();
    const projectId = selectedProjectId();
    if (!className || !projectId) {
      warn('[TagsExplorePanel] Cannot save thumbs: missing class or project');
      return;
    }
    try {
      const response = await putData(`${apiEndpoints.admin.events}/tags`, {
        class_name: className,
        project_id: projectId,
        events: [ev.event_id],
        event_types: eventTypesForDatasetTagPut(ev),
        key: 'thumbsup',
        value: next,
      });
      if (response.success) {
        const newTags = mergeThumbsIntoTagsJson(ev.tags, next);
        const newEv: DatasetEvent = { ...ev, tags: newTags };
        setRows((prev) =>
          prev.map((r) =>
            r.kind === 'dataset_event' && r.ev.event_id === ev.event_id ? { ...r, ev: newEv } : r
          )
        );
        debug('[TagsExplorePanel] Saved maneuver thumbsup', { event_id: ev.event_id, next });
      } else {
        logError('[TagsExplorePanel] Failed to save maneuver thumbsup', response.message || 'Unknown error');
      }
    } catch (err) {
      logError('[TagsExplorePanel] applyDatasetThumbs error', err);
    }
  };

  const onDatasetThumbUp = (ev: DatasetEvent, e: MouseEvent) => {
    const cur = datasetThumbsState(ev);
    const next = cur === true ? null : true;
    void applyDatasetThumbs(ev, next, e);
  };

  const onDatasetThumbDown = (ev: DatasetEvent, e: MouseEvent) => {
    const cur = datasetThumbsState(ev);
    const next = cur === false ? null : false;
    void applyDatasetThumbs(ev, next, e);
  };

  const modalThumbUpActive = createMemo(() => editModalDraftThumbs() === true);

  const modalThumbDownActive = createMemo(() => editModalDraftThumbs() === false);

  const onModalThumbUp = (e: MouseEvent) => {
    e.stopPropagation();
    if (!editTarget()) {return;}
    const cur = editModalDraftThumbs();
    setEditModalDraftThumbs(cur === true ? null : true);
  };

  const onModalThumbDown = (e: MouseEvent) => {
    e.stopPropagation();
    if (!editTarget()) {return;}
    const cur = editModalDraftThumbs();
    setEditModalDraftThumbs(cur === false ? null : false);
  };

  const datasetIdsForRaceSave = async (): Promise<number[]> => {
    const selected = selectedDatasetId();
    if (selected > 0) {return [selected];}
    const className = selectedClassName();
    const projectId = selectedProjectId();
    const dateVal = (selectedDate() || '').trim();
    if (!className || !projectId || dateVal === '') {return [];}
    const dateForApi = dateVal.replace(/[-/]/g, '');
    const url = `${apiEndpoints.app.datasets}/date/datasets_with_duration?class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(String(projectId))}&date=${encodeURIComponent(dateForApi)}`;
    const res = await getData(url);
    if (!res.success || !Array.isArray(res.data)) {return [];}
    const ids: number[] = [];
    for (const row of res.data as Array<{ dataset_id?: number }>) {
      const id = Number(row?.dataset_id);
      if (Number.isFinite(id) && id > 0) {ids.push(id);}
    }
    return ids;
  };

  /** Move the start time on the tagger row and on each matching dataset race/prestart. No processing. */
  const saveRaceOrPrestartStart = async (
    eventType: 'RACE' | 'PRESTART',
    editedUser: ExploreUserEventRow | null,
    editedDataset: TagsDatasetEvent | null
  ): Promise<boolean> => {
    const className = selectedClassName();
    const projectId = selectedProjectId();
    if (!className || !projectId) {
      setEditErrorMsg('Missing class or project.');
      return false;
    }
    const startIso = datetimeLocalValueToIso(editStartTimeLocal().trim());
    if (!startIso) {
      setEditErrorMsg('Please enter a valid start time.');
      return false;
    }
    const editedTags = editedUser
      ? (editedUser.tags as Record<string, unknown> | null)
      : parseDatasetEventTags(editedDataset?.tags);
    const raceNumber = raceTagNumber(editedTags, eventType);

    const userTargets = new Map<number, ExploreUserEventRow>();
    if (editedUser && editedUser.user_event_id > 0) {
      userTargets.set(editedUser.user_event_id, editedUser);
    }
    if (raceNumber != null) {
      for (const row of rows()) {
        if (row.kind !== 'user_event') {continue;}
        const user = row.user;
        if ((user.event_type || '').trim().toUpperCase() !== eventType) {continue;}
        if (raceTagNumber(user.tags as Record<string, unknown> | null, eventType) !== raceNumber) {continue;}
        if (user.user_event_id > 0) {userTargets.set(user.user_event_id, user);}
      }
    }

    let userFailed = false;
    const savedUsers = new Map<number, ExploreUserEventRow>();
    for (const user of userTargets.values()) {
      const moved = movedIntervalTimes(user.start_time, user.end_time, user.focus_time, startIso);
      if (!moved) {
        userFailed = true;
        continue;
      }
      const updated = await putUserEventExploreUpdate(projectId, user, {}, {
        date: moved.date,
        focus_time: moved.focus_time,
        start_time: moved.start_time,
        end_time: moved.end_time,
      });
      if (!updated) {
        userFailed = true;
        continue;
      }
      savedUsers.set(user.user_event_id, {
        ...updated,
        date: moved.date,
        focus_time: moved.focus_time,
        start_time: moved.start_time,
        end_time: moved.end_time,
      });
    }

    const windows: Array<{ event_id: number; start_time: string; end_time: string | null }> = [];
    if (raceNumber != null) {
      const datasetIds = await datasetIdsForRaceSave();
      for (const datasetId of datasetIds) {
        const url = `${apiEndpoints.app.events}/info?class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(String(projectId))}&dataset_id=${encodeURIComponent(String(datasetId))}&event_type=${encodeURIComponent(eventType)}`;
        const res = await getData(url);
        if (!res.success || !Array.isArray(res.data)) {continue;}
        for (const raw of res.data as Array<{ event_id?: number; start_time?: string; end_time?: string | null; tags?: unknown }>) {
          const id = Number(raw?.event_id);
          if (!Number.isFinite(id) || id <= 0) {continue;}
          if (raceTagNumber(parseDatasetEventTags(raw.tags as DatasetEvent['tags']), eventType) !== raceNumber) {continue;}
          windows.push({
            event_id: id,
            start_time: String(raw.start_time ?? ''),
            end_time: raw.end_time ?? null,
          });
        }
      }
    }
    if (windows.length === 0 && editedDataset && editedDataset.event_id > 0) {
      windows.push({
        event_id: editedDataset.event_id,
        start_time: editedDataset.start_time,
        end_time: editedDataset.end_time ?? null,
      });
    }

    let datasetFailed = false;
    const savedDatasetIds = new Set<number>();
    for (const windowRow of windows) {
      const moved = movedIntervalTimes(windowRow.start_time, windowRow.end_time, null, startIso);
      if (!moved) {
        datasetFailed = true;
        continue;
      }
      const res = await putData(`${apiEndpoints.admin.events}/dataset-event-window`, {
        class_name: className,
        project_id: projectId,
        event_id: windowRow.event_id,
        start_time: moved.start_time,
        end_time: moved.end_time ?? moved.start_time,
      });
      if (!res.success) {
        datasetFailed = true;
        warn('[TagsExplorePanel] Dataset event window update failed', res.message);
        continue;
      }
      savedDatasetIds.add(windowRow.event_id);
    }

    setRows((prev) => prev.map((row) => {
      if (row.kind === 'user_event') {
        const saved = savedUsers.get(row.user.user_event_id);
        if (!saved) {return row;}
        return { ...row, user: saved, sortMs: userEventSortMs(saved, tagsTimezone()) };
      }
      if (row.kind !== 'dataset_event') {return row;}
      const rowType = (row.ev.event_type || '').trim().toUpperCase();
      if (rowType !== eventType) {return row;}
      const rowNumber = raceTagNumber(parseDatasetEventTags(row.ev.tags), rowType);
      const matches = savedDatasetIds.has(row.ev.event_id)
        || (raceNumber != null && rowNumber === raceNumber && savedDatasetIds.size > 0);
      if (!matches) {return row;}
      const moved = movedIntervalTimes(row.ev.start_time, row.ev.end_time, row.ev.focus_time, startIso);
      if (!moved) {return row;}
      const ev: TagsDatasetEvent = {
        ...row.ev,
        start_time: moved.start_time,
        end_time: moved.end_time ?? row.ev.end_time,
        focus_time: moved.focus_time,
      };
      return { ...row, ev, sortMs: datasetEventSortMs(ev, tagsTimezone()) };
    }));

    if (userTargets.size === 0 && windows.length === 0) {
      setEditErrorMsg('No user or dataset event to update.');
      return false;
    }
    if (userFailed || datasetFailed) {
      setEditErrorMsg('The start time was not saved on every matching event.');
      return false;
    }
    debug('[TagsExplorePanel] Saved race/prestart start', {
      eventType,
      raceNumber,
      users: savedUsers.size,
      datasets: savedDatasetIds.size,
    });
    return true;
  };

  const saveComment = async () => {
    const target = editTarget();
    if (!target || target.kind === 'media') {return;}
    const projectId = selectedProjectId();
    const className = selectedClassName();
    setEditErrorMsg(null);
    setEditSaving(true);
    try {
      if (target.kind === 'user_event') {
        const et = (target.user.event_type || '').trim().toUpperCase();
        if (et === 'RACE' || et === 'PRESTART') {
          const ok = await saveRaceOrPrestartStart(et, target.user, null);
          if (ok) {setEditTarget(null);}
          return;
        }
        const commentText = editText().trim();
        if (!taggerEventTypeAllowsEmptyComment(et) && taggerUserEventShowsCommentField(et) && commentText === '') {
          setEditErrorMsg('Please enter a note.');
          return;
        }
        if (et === 'TEST') {
          const testCommentErr = taggerValidateTestComment(commentText);
          if (testCommentErr) {
            setEditErrorMsg(testCommentErr);
            return;
          }
        }
        const built = buildExploreUserEventUpdateFromForm(target.user, {
          focusLocal: editFocusTimeLocal(),
          startLocal: editStartTimeLocal(),
          endLocal: editEndTimeLocal(),
          raceNumberLocal: editRaceNumberLocal(),
        });
        if ('error' in built) {
          setEditErrorMsg(built.error);
          return;
        }
        const tagsPatch: Record<string, unknown> = { ...built.tagsPatch };
        if (et === 'CREW') {
          let form = crewEditValues();
          const seatTags = crewTagsFromEditValues(form);
          let coded: Record<string, unknown> | null = null;
          if (className && projectId) {
            const [roster, crewList] = await Promise.all([
              fetchCrewRoster(className, projectId),
              fetchCrews(className, projectId),
            ]);
            if (roster && crewList) {
              const ensured = await ensureCrewConfigForLineup(className, projectId, seatTags, roster, crewList);
              if (!ensured.ok) {
                warn('[TagsExplorePanel] Could not add a crew config', ensured.message);
                toastStore.showToast('warning', ensured.message);
              }
              const list = ensured.ok ? ensured.crews : crewList;
              if (ensured.ok && ensured.created) {
                debug('[TagsExplorePanel] Added crew config', {
                  name: ensured.created.name,
                  crew_id: ensured.created.crew_id,
                });
                toastStore.showToast('success', `Crew code ${ensured.created.name} added`);
              }
              const applied = applyCrewCode(seatTags, roster, list);
              if (applied !== seatTags) {
                coded = applied;
                if (applied.crew_code != null) {
                  form = { ...form, crew: String(applied.crew_code) };
                }
              }
            }
          }
          const crewPatch = crewTagEditPatch(target.user.tags ?? target.user.comment, form);
          if (coded) {
            crewPatch.Crew_id = coded.Crew_id == null ? null : coded.Crew_id;
            for (const seat of CREW_SEATS) {
              const key = `${seat}_id`;
              crewPatch[key] = coded[key] == null ? null : coded[key];
            }
          }
          Object.assign(tagsPatch, crewPatch);
        }
        if (editTargetShowsThumbs()) {
          tagsPatch.thumbsup = editModalDraftThumbs();
        }
        if (taggerUserEventShowsCommentField(et)) {
          tagsPatch.Comment = commentText;
        }
        const updated = await putUserEventExploreUpdate(projectId, target.user, tagsPatch, built.times);
        if (updated) {
          setRows((prev) =>
            prev.map((r) =>
              r.kind === 'user_event' && r.user.user_event_id === target.user.user_event_id
                ? { ...r, user: updated, sortMs: userEventSortMs(updated, tagsTimezone()) }
                : r
            )
          );
          setEditTarget(null);
        }
      } else {
        if (!className || !projectId) {
          warn('[TagsExplorePanel] Cannot save maneuver NOTE: missing class or project');
          return;
        }
        const ev = target.ev;
        const datasetEt = (ev.event_type || '').trim().toUpperCase();
        if (datasetEt === 'RACE' || datasetEt === 'PRESTART') {
          const ok = await saveRaceOrPrestartStart(datasetEt, null, ev);
          if (ok) {setEditTarget(null);}
          return;
        }
        const draftThumbs = editModalDraftThumbs();
        const tagPutTypes = eventTypesForDatasetTagPut(ev);
        const noteResp = await putData(`${apiEndpoints.admin.events}/tags`, {
          class_name: className,
          project_id: projectId,
          events: [ev.event_id],
          event_types: tagPutTypes,
          key: 'NOTE',
          value: editText(),
        });
        if (!noteResp.success) {
          logError('[TagsExplorePanel] Failed to save maneuver NOTE', noteResp.message || 'Unknown error');
          return;
        }
        let newTags = mergeNoteIntoTagsJson(ev.tags, editText());
        if (isManeuverDatasetEvent(ev)) {
          const thumbResp = await putData(`${apiEndpoints.admin.events}/tags`, {
            class_name: className,
            project_id: projectId,
            events: [ev.event_id],
            event_types: tagPutTypes,
            key: 'thumbsup',
            value: draftThumbs,
          });
          if (thumbResp.success) {
            newTags = mergeThumbsIntoTagsJson(newTags, draftThumbs);
          } else {
            logError('[TagsExplorePanel] Failed to save maneuver thumbsup', thumbResp.message || 'Unknown error');
          }
        }
        const newEv: DatasetEvent = { ...ev, tags: newTags };
        setRows((prev) =>
          prev.map((r) =>
            r.kind === 'dataset_event' && r.ev.event_id === ev.event_id ? { ...r, ev: newEv } : r
          )
        );
        setEditTarget(null);
        debug('[TagsExplorePanel] Saved maneuver NOTE and thumbs', { event_id: ev.event_id });
      }
    } catch (e) {
      logError('[TagsExplorePanel] saveComment error', e);
    } finally {
      setEditSaving(false);
    }
  };

  /** Modal footer: both thumbs; clicking one selects it (switches from the other) or clears if already active. */
  const thumbButtons = (opts: {
    upActive: boolean;
    downActive: boolean;
    onUp: (e: MouseEvent) => void;
    onDown: (e: MouseEvent) => void;
    saving?: boolean;
  }) => (
    <>
      <button
        type="button"
        class="tags-explore-icon-btn tags-explore-icon-btn--thumb-up"
        classList={{ 'tags-explore-icon-btn--active': opts.upActive }}
        title="Thumbs up"
        aria-label="Thumbs up"
        aria-pressed={opts.upActive}
        disabled={Boolean(opts.saving)}
        onClick={(e) => opts.onUp(e)}
      >
        <FaSolidThumbsUp size={16} aria-hidden="true" />
      </button>
      <button
        type="button"
        class="tags-explore-icon-btn tags-explore-icon-btn--thumb-down"
        classList={{ 'tags-explore-icon-btn--active': opts.downActive }}
        title="Thumbs down"
        aria-label="Thumbs down"
        aria-pressed={opts.downActive}
        disabled={Boolean(opts.saving)}
        onClick={(e) => opts.onDown(e)}
      >
        <FaSolidThumbsDown size={16} aria-hidden="true" />
      </button>
    </>
  );

  /** Next to bubble: only the active vote (click to clear). */
  const thumbButtonInline = (opts: {
    vote: true | false;
    onUp: (e: MouseEvent) => void;
    onDown: (e: MouseEvent) => void;
  }) => (
    <div class="tags-explore-entry__icons tags-explore-entry__icons--trailing tags-explore-entry__icons--inline-single">
      {opts.vote === true ? (
        <button
          type="button"
          class="tags-explore-icon-btn tags-explore-icon-btn--thumb-up"
          classList={{ 'tags-explore-icon-btn--active': true }}
          title="Thumbs up — click to remove"
          aria-label="Thumbs up — click to remove"
          aria-pressed={true}
          onClick={(e) => opts.onUp(e)}
        >
          <FaSolidThumbsUp size={16} aria-hidden="true" />
        </button>
      ) : (
        <button
          type="button"
          class="tags-explore-icon-btn tags-explore-icon-btn--thumb-down"
          classList={{ 'tags-explore-icon-btn--active': true }}
          title="Thumbs down — click to remove"
          aria-label="Thumbs down — click to remove"
          aria-pressed={true}
          onClick={(e) => opts.onDown(e)}
        >
          <FaSolidThumbsDown size={16} aria-hidden="true" />
        </button>
      )}
    </div>
  );

  return (
    <div
      class="tags-explore-panel"
      classList={{ 'tags-explore-panel--edit-mode': editModeEnabled() }}
    >
      <div class="tags-explore-panel__header">
        <div class="tags-explore-panel__header-row">
          <h2 class="tags-explore-panel__header-title">Tags</h2>
          <div class="tags-explore-panel__header-search">
            <input
              id="tags-explore-search"
              type="search"
              class="tags-explore-panel__search-input"
              placeholder="Filter by text, user, type, time…"
              aria-label="Search tags"
              value={searchTerm()}
              onInput={(e) => setSearchTerm(e.currentTarget.value)}
            />
          </div>
          <button
            type="button"
            class="tags-explore-panel__settings-btn"
            title="Tag page settings"
            aria-label="Tag page settings"
            onClick={() => setSettingsOpen(true)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" aria-hidden="true">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M12 15a3 3 0 100-6 3 3 0 000 6z"
              />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"
              />
            </svg>
          </button>
        </div>
      </div>
      <div class="tags-explore-panel__body">
        <Show when={loading()}>
          <div class="tags-explore-panel__loading">Loading…</div>
        </Show>
        <Show when={!loading() && errorMsg()}>
          <div class="tags-explore-panel__empty">{errorMsg()}</div>
        </Show>
        <div class="tags-explore-panel__scroll">
        <Show when={!loading() && !errorMsg() && rows().length === 0}>
          <div class="tags-explore-panel__empty">No tags for this context.</div>
        </Show>
        <Show when={tagsListEmptyReason() === 'time'}>
          <div class="tags-explore-panel__empty">No tags in the current map time range.</div>
        </Show>
        <Show when={tagsListEmptyReason() === 'coverage'}>
          <div class="tags-explore-panel__empty">
            No tags on the loaded track in this view (times outside continuous data are hidden).
          </div>
        </Show>
        <Show when={tagsListEmptyReason() === 'filters'}>
          <div class="tags-explore-panel__empty">No tags match your filters.</div>
        </Show>
        <For each={displayRowsWithGroups()}>
          {(entry) => {
            const item = entry.item;
            const isGroupStart = entry.isGroupStart;
            const groupClass = isGroupStart ? 'tagger-msg--group-start' : 'tagger-msg--group-cont';
            const pillLabel = rowDisplayUserName(item);
            const pillStyle = () =>
              taggerAuthorPillStylesByOrdinal(
                tagsExploreAuthorOrdinalMap().get(taggerAuthorNormalizedKey(pillLabel)) ?? 0
              );

            if (item.kind === 'user_event') {
              const row = item.user;
              const kindUpper = (row.event_type || '').toUpperCase();
              const primary = userEventPrimaryLine(row);
              const showPrimary = primary.length > 0 && primary.toUpperCase() !== kindUpper;
              const sub = userEventCommentSubline(row);
              const t = tagThumbsFromTags(row.tags as Record<string, unknown> | null);
              const showTail = isGroupStart;
              return (
                <div class="tags-explore-entry">
                  <div class="tags-explore-entry__bubble-wrap">
                    <article
                      class={`tagger-msg ${groupClass}`}
                      onClick={(e) => onUserRowClick(row, e)}
                    >
                      <div class="tagger-msg__swipe-wrap">
                        <div class="tagger-msg__grid">
                          <div class="tagger-msg__name-col">
                            <Show when={isGroupStart}>
                              <span class="tagger-msg__name-pill" style={pillStyle()} title={pillLabel}>
                                {pillLabel}
                              </span>
                            </Show>
                          </div>
                          <div class="tagger-msg__bubble-row">
                            <div
                              class={`tagger-msg__bubble ${bubbleClassForEventType(row.event_type)}${showTail ? ' tagger-msg__bubble--tail' : ''
                                }`}
                            >
                              <div class="tagger-msg__bubble-meta tags-explore-panel__bubble-meta">
                                <div class="tags-explore-panel__kind-row">
                                  <span class="tagger-msg__kind">{kindUpper}</span>
                                  <Show when={editModeEnabled()}>
                                    <button
                                      type="button"
                                      class="tags-explore-entry__edit-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openUserEventEditor(row);
                                      }}
                                    >
                                      Edit
                                    </button>
                                  </Show>
                                </div>
                                <span class="tagger-msg__time">{fmtTimeOnly(row.focus_time ?? row.start_time, tagsTimezone())}</span>
                              </div>
                              <Show when={showPrimary}>
                                <p
                                  class="tagger-msg__text"
                                  classList={{ 'tags-explore-panel__crew-list': kindUpper === 'CREW' }}
                                >
                                  {primary}
                                </p>
                              </Show>
                              <Show when={sub}>
                                <p class="tagger-msg__text tags-explore-panel__subline">{sub}</p>
                              </Show>
                            </div>
                            <Show when={t === true}>{thumbButtonInline({ vote: true, onUp: (e) => onUserThumbUp(row, e), onDown: (e) => onUserThumbDown(row, e) })}</Show>
                            <Show when={t === false}>{thumbButtonInline({ vote: false, onUp: (e) => onUserThumbUp(row, e), onDown: (e) => onUserThumbDown(row, e) })}</Show>
                          </div>
                        </div>
                      </div>
                    </article>
                  </div>
                </div>
              );
            }

            if (item.kind === 'media') {
              const media = item.media;
              const kindUpper = 'MEDIA';
              const msg = (media.mediaSource || '').trim() || '—';
              const showTail = isGroupStart;
              return (
                <div class="tags-explore-entry tags-explore-entry--dataset">
                  <div class="tags-explore-entry__bubble-wrap">
                    <article
                      class={`tagger-msg ${groupClass}`}
                      onClick={(e) => onMediaRowClick(media, e)}
                    >
                      <div class="tagger-msg__swipe-wrap">
                        <div class="tagger-msg__grid">
                          <div class="tagger-msg__name-col">
                            <Show when={isGroupStart}>
                              <span class="tagger-msg__name-pill" style={pillStyle()} title={pillLabel}>
                                {pillLabel}
                              </span>
                            </Show>
                          </div>
                          <div class="tagger-msg__bubble-row">
                            <div
                              class={`tagger-msg__bubble ${bubbleClassForEventType('MEDIA')}${
                                showTail ? ' tagger-msg__bubble--tail' : ''
                              }`}
                            >
                              <div class="tagger-msg__bubble-meta tags-explore-panel__bubble-meta">
                                <div class="tags-explore-panel__kind-row">
                                  <span class="tagger-msg__kind">{kindUpper}</span>
                                  <Show when={editModeEnabled()}>
                                    <button
                                      type="button"
                                      class="tags-explore-entry__edit-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openMediaEditor(media);
                                      }}
                                    >
                                      Edit
                                    </button>
                                  </Show>
                                </div>
                                <span class="tagger-msg__time">{fmtTimeOnly(media.start.toISOString(), tagsTimezone())}</span>
                              </div>
                              <p class="tagger-msg__text">{msg}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </article>
                  </div>
                </div>
              );
            }

            const ev = item.ev;
            const kindUpper = (ev.event_type || '').toUpperCase();
            const sub = noteTextFromDatasetEvent(ev).trim() || null;
            const t = datasetThumbsState(ev);
            const showTail = isGroupStart;
            return (
              <div class="tags-explore-entry tags-explore-entry--dataset">
                <div class="tags-explore-entry__bubble-wrap">
                  <article
                    class={`tagger-msg ${groupClass}`}
                    onClick={(e) => onDatasetRowClick(ev, e)}
                  >
                    <div class="tagger-msg__swipe-wrap">
                      <div class="tagger-msg__grid">
                        <div class="tagger-msg__name-col">
                          <Show when={isGroupStart}>
                            <span class="tagger-msg__name-pill" style={pillStyle()} title={pillLabel}>
                              {pillLabel}
                            </span>
                          </Show>
                        </div>
                        <div class="tagger-msg__bubble-row">
                          <div
                            class={`tagger-msg__bubble ${bubbleClassForEventType(ev.event_type)}${showTail ? ' tagger-msg__bubble--tail' : ''
                              }`}
                          >
                            <div class="tagger-msg__bubble-meta tags-explore-panel__bubble-meta">
                              <div class="tags-explore-panel__kind-row">
                                <span class="tagger-msg__kind">{kindUpper}</span>
                                <Show when={editModeEnabled()}>
                                  <button
                                    type="button"
                                    class="tags-explore-entry__edit-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openDatasetEventEditor(ev);
                                    }}
                                  >
                                    Edit
                                  </button>
                                </Show>
                              </div>
                              <span class="tagger-msg__time">
                                {fmtTimeOnly(
                                  kindUpper === 'PRESTART'
                                    ? ev.end_time ?? ev.start_time
                                    : ev.start_time
                                  , tagsTimezone()
                                )}
                              </span>
                            </div>
                            <Show when={sub}>
                              <p class="tagger-msg__text tags-explore-panel__subline">{sub}</p>
                            </Show>
                          </div>
                          <Show when={t === true}>{thumbButtonInline({ vote: true, onUp: (e) => onDatasetThumbUp(ev, e), onDown: (e) => onDatasetThumbDown(ev, e) })}</Show>
                          <Show when={t === false}>{thumbButtonInline({ vote: false, onUp: (e) => onDatasetThumbUp(ev, e), onDown: (e) => onDatasetThumbDown(ev, e) })}</Show>
                        </div>
                      </div>
                    </div>
                  </article>
                </div>
              </div>
            );
          }}
        </For>
        </div>
      </div>

      <Show when={settingsOpen()}>
        <div class="tags-explore-settings-modal-backdrop" role="presentation">
          <button
            type="button"
            class="tags-explore-settings-modal-backdrop__shade"
            aria-label="Close settings"
            onClick={() => setSettingsOpen(false)}
          />
          <div
            class="tags-explore-settings-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tags-explore-settings-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="tags-explore-settings-title">Tag page settings</h3>
            <div class="tags-explore-settings-modal__field">
              <label for="tags-explore-settings-user">User</label>
              <select
                id="tags-explore-settings-user"
                value={selectedUser()}
                onChange={(e) => setSelectedUser(e.currentTarget.value)}
              >
                <option value="all">All users</option>
                <For each={uniqueUsers()}>{(u) => <option value={u}>{u}</option>}</For>
              </select>
            </div>
            <div class="tags-explore-settings-modal__field">
              <label for="tags-explore-settings-type">Event type</label>
              <select
                id="tags-explore-settings-type"
                value={selectedEventType()}
                onChange={(e) => setSelectedEventType(e.currentTarget.value)}
              >
                <option value="all">All types</option>
                <For each={uniqueEventTypes()}>{(t) => <option value={t}>{t}</option>}</For>
              </select>
            </div>
            <div class="tags-explore-settings-modal__field tags-explore-settings-modal__field--checkbox">
              <label class="tags-explore-settings-modal__checkbox-label">
                <input
                  id="tags-explore-settings-edit-mode"
                  type="checkbox"
                  checked={editModeEnabled()}
                  onChange={(e) => setEditModeEnabled(e.currentTarget.checked)}
                />
                <span>Edit mode</span>
              </label>
              <p class="tags-explore-settings-modal__hint">
                When on, hover a tag to reveal Edit and change focus time, notes, and thumbs.
              </p>
            </div>
            <div class="tags-explore-settings-modal__field tags-explore-settings-modal__field--checkbox">
              <label class="tags-explore-settings-modal__checkbox-label">
                <input
                  id="tags-explore-settings-show-all"
                  type="checkbox"
                  checked={showAllTags()}
                  onChange={(e) => setShowAllTags(e.currentTarget.checked)}
                />
                <span>Show all</span>
              </label>
              <p class="tags-explore-settings-modal__hint">
                When off, hides tags in time ranges with no continuous track data (same gap logic as the map).
              </p>
            </div>
            <div class="tags-explore-settings-modal__actions">
              <button type="button" onClick={() => setSettingsOpen(false)}>
                Done
              </button>
            </div>
          </div>
        </div>
      </Show>

      <Show when={editTarget()}>
        <div class="explore-map-user-event-modal-backdrop" role="presentation">
          <div
            class="explore-map-user-event-modal tags-explore-edit-modal"
            role="dialog"
            aria-modal="true"
          >
            <Show
              when={editTarget()?.kind !== 'media'}
              fallback={
                <>
                  <h3>Edit tag</h3>
                  <p class="tags-explore-edit-modal__readonly-hint">
                    Short media clips cannot be edited from the Tags panel.
                  </p>
                  <div class="explore-map-user-event-modal-actions tags-explore-panel__comment-modal-footer">
                    <div class="tags-explore-panel__comment-modal-actions-right">
                      <button type="button" onClick={() => setEditTarget(null)}>
                        Close
                      </button>
                    </div>
                  </div>
                </>
              }
            >
              <h3>Edit tag</h3>
              <Show when={editErrorMsg()}>
                <p class="tags-explore-edit-modal__error" role="alert">
                  {editErrorMsg()}
                </p>
              </Show>
              <Show when={editTargetShowsCrewFields()}>
                <TagsExploreEditCrewFields values={crewEditValues} setField={setCrewEditField} />
              </Show>
              <Show when={editTargetShowsTimeFields()}>
                <TagsExploreEditTimeFields
                  eventTypeUpper={editTargetEventTypeUpper}
                  focusLocal={editFocusTimeLocal}
                  setFocusLocal={setEditFocusTimeLocal}
                  startLocal={editStartTimeLocal}
                  setStartLocal={setEditStartTimeLocal}
                  endLocal={editEndTimeLocal}
                  setEndLocal={setEditEndTimeLocal}
                  raceNumberLocal={editRaceNumberLocal}
                  setRaceNumberLocal={setEditRaceNumberLocal}
                  startOnly={editTargetStartOnly}
                />
              </Show>
              <Show when={editTargetShowsCommentField()}>
                <Show when={editTargetEventTypeUpper() === 'TEST'}>
                  <label class="tags-explore-edit-modal__comment-label" for="tags-explore-edit-comment">
                    Comment
                  </label>
                </Show>
                <textarea
                  id="tags-explore-edit-comment"
                  class="tags-explore-panel__modal-textarea"
                  value={editText()}
                  maxlength={editTargetEventTypeUpper() === 'TEST' ? TAGGER_TEST_COMMENT_MAX_LENGTH : undefined}
                  onInput={(e) => {
                    const raw = e.currentTarget.value;
                    setEditText(editTargetEventTypeUpper() === 'TEST' ? taggerClampTestCommentInput(raw) : raw);
                  }}
                  rows={editTargetEventTypeUpper() === 'TEST' ? 2 : 5}
                  placeholder={editTargetEventTypeUpper() === 'TEST' ? 'Comment' : 'Note'}
                />
              </Show>
              <div class="explore-map-user-event-modal-actions tags-explore-panel__comment-modal-footer">
                <Show when={editTargetShowsThumbs()}>
                  <div class="tags-explore-panel__comment-modal-thumbs" onClick={(e) => e.stopPropagation()}>
                    {thumbButtons({
                      upActive: modalThumbUpActive(),
                      downActive: modalThumbDownActive(),
                      onUp: (e) => onModalThumbUp(e),
                      onDown: (e) => onModalThumbDown(e),
                      saving: editSaving(),
                    })}
                  </div>
                </Show>
                <div class="tags-explore-panel__comment-modal-actions-right">
                  <button type="button" disabled={editSaving()} onClick={() => setEditTarget(null)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    class="explore-map-user-event-primary"
                    disabled={editSaving()}
                    onClick={() => void saveComment()}
                  >
                    Save
                  </button>
                </div>
              </div>
            </Show>
          </div>
        </div>
      </Show>
    </div>
  );
}
